import { ensureTab, getRows, appendRow, updateRow, getSheetIdByTitle, sheetsRequest } from '../lib/googleSheets';

const TAB_NAME = 'Products';
const FIELD_NAMES = [
  'ProductID', 'ProductName', 'AggregateQty', 'SitePrice', 'SiteCost', 'UPC', 'ASIN', 
  'KitComponents', 'KitParents', 'ShadowOf', 'Categories', 'WebsiteViewURL', 
  'CompanyName', 'AmazonViewURL', 'GalleryImageURL', 'WalmartMarketplaceViewURL', 
  'AmazonEnabled', 'eBayEnabled', 'EtsyEnabled', 'WalmartAPIEnabled', 'WebEnabled', 
  'CreationDate', 'Location'
];

const mapRowToProduct = (row, headerMap) => {
  const p = {};
  FIELD_NAMES.forEach(field => {
    const colIdx = headerMap[field.toLowerCase()];
    p[field] = colIdx !== undefined ? (row[colIdx] || '') : '';
  });
  
  const cleanId = (p.ProductID || '').trim();
  
  return {
    ...p,
    id: cleanId,
    title: p.ProductName || 'Unnamed Product',
    image: p.GalleryImageURL?.trim() || '',
    stock: parseInt(p.AggregateQty || 0),
    price: parseFloat(String(p.SitePrice).replace(/[^0-9.-]/g, '') || 0),
    cost: parseFloat(String(p.SiteCost).replace(/[^0-9.-]/g, '') || 0),
    location: p.Location,
    sku: cleanId,
    status: parseInt(p.AggregateQty) > 10 ? 'In Stock' : (parseInt(p.AggregateQty) > 0 ? 'Low Stock' : 'Out of Stock')
  };
};

export const productService = {
  async getHeaderMap() {
    const rows = await getRows(`${TAB_NAME}!A1:Z1`);
    if (!rows || rows.length === 0) {
      await ensureTab(TAB_NAME, FIELD_NAMES);
      return FIELD_NAMES.reduce((acc, name, i) => ({ ...acc, [name.toLowerCase()]: i }), {});
    }
    const headers = rows[0];
    return headers.reduce((acc, name, i) => {
      if (name) acc[name.toLowerCase().trim()] = i;
      return acc;
    }, {});
  },

  async getAll() {
    const headerMap = await this.getHeaderMap();
    const rows = await getRows(`${TAB_NAME}!A2:Z`);
    return rows.map(row => mapRowToProduct(row, headerMap));
  },

  async getById(id) {
    const products = await this.getAll();
    return products.find(p => p.id === String(id).trim()) || null;
  },

  async _findRowIndex(id) {
    const headerMap = await this.getHeaderMap();
    const idColIdx = headerMap['productid'];
    if (idColIdx === undefined) return -1;
    const rows = await getRows(`${TAB_NAME}!A:Z`);
    for (let i = 1; i < rows.length; i++) {
      if (rows[i] && rows[i][idColIdx] && String(rows[i][idColIdx]).trim() === String(id).trim()) {
        return i + 1;
      }
    }
    return -1;
  },

  async create(productData) {
    const headerMap = await this.getHeaderMap();
    const headers = await getRows(`${TAB_NAME}!A1:Z1`).then(r => r[0]);
    const row = new Array(headers.length).fill('');
    Object.entries(productData).forEach(([key, val]) => {
      const idx = headerMap[key.toLowerCase()];
      if (idx !== undefined) row[idx] = val;
    });
    await appendRow(`${TAB_NAME}!A:Z`, row);
    return productData;
  },

  async update(id, productData) {
    const index = await this._findRowIndex(id);
    if (index === -1) throw new Error('Product not found');
    const headerMap = await this.getHeaderMap();
    const existingRows = await getRows(`${TAB_NAME}!A${index}:Z${index}`);
    const row = existingRows[0] ? [...existingRows[0]] : [];
    Object.entries(productData).forEach(([key, val]) => {
      const idx = headerMap[key.toLowerCase()];
      if (idx !== undefined) row[idx] = val;
    });
    await updateRow(`${TAB_NAME}!A${index}:Z${index}`, row);
    return productData;
  },

  async seed(initialData) {
    const rows = await getRows(`${TAB_NAME}!A:Z`);
    if (!rows || rows.length <= 1) {
      for (const item of initialData) {
        await this.create({
          ProductID: item.sku || item.id,
          ProductName: item.title,
          GalleryImageURL: item.image,
          AggregateQty: item.stock,
          SitePrice: item.price,
          CreationDate: new Date().toISOString()
        });
      }
    }
  }
};